import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Download, FileText, Printer, Eye } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { AppLayout } from "@/components/AppLayout";
import { CollaborativeEditor } from "@/components/CollaborativeEditor";
import { DocumentViewer } from "@/components/DocumentViewer";

const DocumentEditor = () => {
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState<string>('');
  const [showViewer, setShowViewer] = useState(false);
  const { toast } = useToast();

  const handleEditorChange = (newContent: string) => {
    setContent(newContent);
  };

  const handleSave = () => {
    // Save functionality here
    toast({
      title: "Document Saved",
      description: "Your document has been saved successfully.",
    });
  };

  const handleDownload = () => {
    // Download as Word doc functionality
    toast({
      title: "Download Started",
      description: "Your document is being prepared for download.",
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      const url = URL.createObjectURL(file);
      setFileUrl(url);
      setShowViewer(true);
      toast({
        title: "File Uploaded",
        description: `${file.name} has been loaded in the viewer.`,
      });
    }
  };

  return (
    <AppLayout currentPage="document-editor">
      <div className="min-h-screen bg-background">
        {/* Header Bar */}
        <div className="border-b border-border bg-card">
          <div className="container max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <FileText className="h-6 w-6 text-primary" />
                  <h1 className="text-xl font-semibold">Document Editor</h1>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleSave}>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrint}>
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </Button>
                <Button variant="default" size="sm" onClick={handleDownload}>
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Content Container */}
        <div className="container max-w-7xl mx-auto p-4 space-y-6">
          {/* PDF Viewer Test Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                PDF Viewer Test
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pdf-upload">Upload PDF or Word Document</Label>
                <Input
                  id="pdf-upload"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileUpload}
                  className="cursor-pointer"
                />
                <p className="text-sm text-muted-foreground">
                  Upload a PDF or Word document to test the viewer functionality
                </p>
              </div>
              
              {showViewer && uploadedFile && fileUrl && (
                <div className="border rounded-lg p-4 bg-muted/30">
                  <h3 className="font-medium mb-3">Document Viewer - {uploadedFile.name}</h3>
                  <DocumentViewer
                    fileUrl={fileUrl}
                    fileName={uploadedFile.name}
                    fileType={uploadedFile.type}
                    className="max-h-96 overflow-hidden"
                  />
                </div>
              )}
            </CardContent>
          </Card>
          
          <Separator />
          
          {/* Editor Container */}
          <Card>
            <CardHeader>
              <CardTitle>Document Editor</CardTitle>
            </CardHeader>
            <CardContent>
              <CollaborativeEditor
                value={content}
                onChange={handleEditorChange}
                placeholder="Start typing your document..."
                className="shadow-lg"
              />
            </CardContent>
          </Card>
        </div>
        
      </div>
    </AppLayout>
  );
};

export default DocumentEditor;